import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Filter } from 'lucide-react';

const EMPTY_LABEL = '(empty)';

// One filter menu per <th>. Owns its open/closed state and the search
// box; the SELECTED set is owned by the page so filters survive across
// re-renders and can be cleared en masse from the toolbar.
//
// Props:
//   label         — the column header (used inside the dropdown title)
//   values        — string[] of unique values from the source rows
//                   (incl. '' for blank cells; the UI renders those as
//                   "(empty)" but the stored selected entry is still '')
//   selected      — Set<string> of currently-active values for this column
//                   (when null / empty, no filter is active)
//   onChange      — fn(Set<string>) — replaces the selected set
//   onClear       — fn() — clears this column's filter
//
// We use the same "active" semantics as Excel: when nothing is checked,
// the filter is OFF and every row passes for this column. As soon as the
// user checks at least one value, only those values pass. There is no
// "All" affordance — clearing the column's filter is how you go back.
export default function ColumnFilterMenu({ label, values, selected, onChange, onClear }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef  = useRef(null);   // the wrapping <span> containing the button
  const panelRef = useRef(null);   // the popover itself (portalled into body)
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0, minWidth: 220 });

  const isActive = selected && selected.size > 0;

  // Place the panel directly under the trigger button. Uses
  // getBoundingClientRect so it's accurate even when the table is
  // horizontally scrolled. The panel is rendered with position: fixed
  // (via the portal), so these are viewport-relative coordinates.
  function positionPanel() {
    const btn = rootRef.current?.querySelector('.col-filter-button');
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const panelWidth = panelRef.current?.offsetWidth || 240;
    const gap = 4;
    // Anchor to the button's right edge; the panel grows leftward and
    // downward. Clamp to viewport so it never escapes off-screen.
    let left = rect.right - panelWidth;
    if (left < 8) left = 8;
    const top = Math.min(rect.bottom + gap, window.innerHeight - 8);
    setPanelPos({ top, left, minWidth: Math.max(220, panelWidth) });
  }

  // Close on outside click + Escape. Outside-click test has to consider
  // BOTH the trigger button AND the portalled panel since they live in
  // different parts of the DOM tree.
  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e) {
      const inTrigger = rootRef.current && rootRef.current.contains(e.target);
      const inPanel   = panelRef.current && panelRef.current.contains(e.target);
      if (!inTrigger && !inPanel) setOpen(false);
    }
    function onKey(e) { if (e.key === 'Escape') setOpen(false); }
    // Reposition on scroll / resize so the panel tracks the button. We
    // could close-on-scroll, but tracking feels less surprising when
    // the user scrolls within the table to find a value.
    function onReflow() { positionPanel(); }
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onReflow, true);   // capture = catches table scroll too
    window.addEventListener('resize', onReflow);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onReflow, true);
      window.removeEventListener('resize', onReflow);
    };
  }, [open]);

  // Recompute when the panel mounts so its width is known before the
  // first paint nudges it leftward.
  useEffect(() => {
    if (open) positionPanel();
  }, [open]);

  useEffect(() => { if (!open) setQuery(''); }, [open]);

  const filteredValues = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return values;
    return values.filter((v) => {
      const display = v === '' ? EMPTY_LABEL : v;
      return display.toLowerCase().includes(q);
    });
  }, [values, query]);

  function toggleValue(v) {
    const next = new Set(selected || []);
    if (next.has(v)) next.delete(v);
    else             next.add(v);
    onChange(next);
  }

  function selectAllVisible() {
    const next = new Set(selected || []);
    for (const v of filteredValues) next.add(v);
    onChange(next);
  }

  function clearHere() {
    onClear();
  }

  return (
    <span className="col-filter" ref={rootRef}>
      <button
        type="button"
        className={`col-filter-button${isActive ? ' is-active' : ''}`}
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        aria-label={isActive ? `Filter active on ${label}` : `Filter ${label}`}
        aria-expanded={open}
        title={isActive ? `${selected.size} value${selected.size === 1 ? '' : 's'} selected` : `Filter ${label}`}
      >
        <Filter size={12} strokeWidth={2} />
        {isActive && <span className="col-filter-dot" />}
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          className="col-filter-panel"
          role="dialog"
          aria-label={`Filter ${label}`}
          style={{
            position: 'fixed',
            top:  panelPos.top,
            left: panelPos.left,
            minWidth: panelPos.minWidth
          }}
        >
          <div className="col-filter-head">
            <span className="col-filter-title">Filter · {label}</span>
            {isActive && (
              <button
                type="button"
                className="col-filter-link"
                onClick={clearHere}
              >
                Clear
              </button>
            )}
          </div>

          <input
            type="search"
            className="col-filter-search"
            placeholder="Search values…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />

          <div className="col-filter-list">
            {filteredValues.length === 0 && (
              <p className="col-filter-empty">No values match.</p>
            )}
            {filteredValues.map((v) => {
              const checked = !!selected?.has(v);
              const display = v === '' ? EMPTY_LABEL : v;
              return (
                <label key={v || '__empty__'} className="col-filter-option">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleValue(v)}
                  />
                  <span>{display}</span>
                </label>
              );
            })}
          </div>

          {filteredValues.length > 0 && (
            <div className="col-filter-foot">
              <button type="button" className="col-filter-link" onClick={selectAllVisible}>
                Select all visible
              </button>
            </div>
          )}
        </div>,
        document.body
      )}
    </span>
  );
}
